import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Api } from 'src/app/services/api';
import { Globals } from 'src/app/services/globals';
import { IStoreCreditTransaction } from '../../../../../../../common/common-interfaces/store-settings';
import { Utility } from '../../../services/utility';

@Component({
    selector: 'credit-transactions',
    templateUrl: './credit-transactions.component.html',
    styleUrls: ['./credit-transactions.component.scss'],
    standalone: false
})
export class CreditTransactionsComponent {
    transactions: IStoreCreditTransaction[];
    periodStart: Date;
    periodEnd: Date;
    transactionTotals: { printCost: number, shippingCost: number, totalCost: number } = { printCost: 0, shippingCost: 0, totalCost: 0 };
    topUpCredits: number = 1000;
    topUpQuote?: {
        credits: number,
        amount: number,
        billingEventValue: number,
        billingEventUnitAmount: number,
        currency: string,
        sourceCurrency: string,
        requestedCurrency: string,
    };
    topUpLoading = false;
    planStatusLoading = false;
    shopifyPlanStatus?: {
        configured: boolean,
        hasActiveSubscription: boolean,
        planSelectionUrl?: string,
        message?: string,
    };
    topUpError = '';
    topUpMessage = '';
    constructor(private api: Api, private router: Router, private route: ActivatedRoute, public globals: Globals, public utility: Utility) {

    }

    async ngOnInit() {
        this.periodStart = moment().subtract(1, 'month').toDate();
        this.periodEnd = moment().toDate();
        const shopifyTopUp = this.route.snapshot.queryParamMap.get('shopifyTopUp');
        if (shopifyTopUp === 'success') {
            this.topUpMessage = 'Shopify top-up completed.';
        } else if (shopifyTopUp) {
            this.topUpError = 'Shopify top-up was not completed.';
        }
        await this.search();
        if (this.isShopifyStore) {
            await this.loadShopifyPlanStatus();
            if (this.canTopUpWithShopify) {
                await this.updateShopifyTopUpQuote();
            }
        }
    }


    async search() {
        this.transactions = await this.api.store().get<any>(`/credit-transactions?startDate=${moment(this.periodStart).startOf('day').toISOString()}&endDate=${moment(this.periodEnd).endOf('day').toISOString()}`);
        this.transactionTotals = { printCost: 0, shippingCost: 0, totalCost: 0 };
        for (let transaction of this.transactions) {
            const printCost = transaction.metadata?.fee?.printCost ?? 0;
            const shippingCost = transaction.metadata?.fee?.shippingCost ?? 0;
            this.transactionTotals.printCost += printCost;
            this.transactionTotals.shippingCost += shippingCost;
            this.transactionTotals.totalCost += printCost + shippingCost;
        }
    }

    async onPeriodChange() {
        if (!this.periodStart || !this.periodEnd) {
            return;
        }

        await this.search();
    }

    get isShopifyStore() {
        return this.globals.storeSettings?.platform === 'shopify';
    }

    get canTopUpWithShopify() {
        return this.shopifyPlanStatus?.hasActiveSubscription === true;
    }

    displayAmount(transaction: IStoreCreditTransaction) {
        return transaction.amount > 0 ? transaction.amount : -transaction.amount;
    }

    async updateShopifyTopUpQuote() {
        this.topUpError = '';
        if (!this.canTopUpWithShopify) {
            this.topUpQuote = undefined;
            return;
        }

        if (!this.topUpCredits || this.topUpCredits <= 0) {
            this.topUpQuote = undefined;
            return;
        }

        try {
            this.topUpQuote = await this.api.store().get<any>(`/credit-transactions/shopify/top-up/quote?credits=${encodeURIComponent(this.topUpCredits)}`);
        }
        catch (e: any) {
            this.topUpQuote = undefined;
            this.topUpError = e?.error?.message || 'Could not prepare Shopify top-up.';
        }
    }

    async loadShopifyPlanStatus() {
        this.planStatusLoading = true;
        this.topUpError = '';
        try {
            this.shopifyPlanStatus = await this.api.store().get<any>('/credit-transactions/shopify/plan-status');
        }
        catch (e: any) {
            this.shopifyPlanStatus = {
                configured: false,
                hasActiveSubscription: false,
                message: e?.error?.message || 'Could not check Shopify app pricing plan.',
            };
        }
        finally {
            this.planStatusLoading = false;
        }
    }

    openShopifyPlanSelection() {
        if (this.shopifyPlanStatus?.planSelectionUrl) {
            window.open(this.shopifyPlanStatus.planSelectionUrl, '_top');
        }
    }

    async startShopifyTopUp() {
        this.topUpLoading = true;
        this.topUpError = '';
        this.topUpMessage = '';
        try {
            const result = await this.api.store().post<any>('/credit-transactions/shopify/top-up', {
                credits: this.topUpCredits,
            });

            this.topUpMessage = 'Shopify top-up billing event accepted. Credits have been added.';
            this.topUpLoading = false;
            if (this.globals.storeSettings) {
                this.globals.storeSettings.credits = (this.globals.storeSettings.credits || 0) + result.quote.credits;
            }
            await this.search();
        }
        catch (e: any) {
            this.topUpError = e?.error?.message || 'Could not start Shopify top-up.';
            this.topUpLoading = false;
        }
    }


}


